import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";

export type ProviderProps<T> = {
  children: ReactNode;
  initValue?: T | T[];
  getValue?: (item: T, level: number) => string | number;
  getLabel?: (item: T, level: number) => string | ReactNode;
  getChildren?: (item: T, level: number) => T[] | undefined;
};

export const SelectContext = createContext({
  selectedValue: undefined,
  isMulty: false,
  active: false,
  setActive: (active) => {},
  getChildren: undefined,
  getValue: undefined,
  getLabel: undefined,
  getParent: undefined,
  options: [],
  onChange: (a: any) => {},
  onCallback: (a: any) => {}
});
function SelectProvider<T>(props: {
  children: ReactNode;
  options: T[];
  initValue?: T;
  isMulty: boolean;
  getValue?: (item: T) => string | number;
  getLabel?: (item: T) => string | ReactNode;
  getChildren?: (item: T) => T[] | undefined;
  getParent?: (item: T) => T | null | undefined;
  onChange: (item: T) => void;
}) {
  const {
    children,
    initValue,
    getChildren = defaultGetChildren,
    getValue = defaultGetValue,
    getLabel = defaultGetLable,
    getParent = defaultGetParent,
    options,
    isMulty,
    onChange
  } = props;
  const [active, setActive] = useState(false);
  const [value, setValue] = useState(initValue);
  const [searchValue, setSearchValue] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const defaultValue = isMulty ? [] : undefined;

  useEffect(() => {
    setValue(initValue);
  }, [initValue]);

  return (
    <SelectContext.Provider
      value={{
        selectedValue: value ?? defaultValue,
        isMulty,
        active,
        setActive,
        getChildren,
        getValue,
        getLabel,
        getParent,
        options,
        onChange: setValue,
        onCallback: onChange
      }}
    >
      {children}
    </SelectContext.Provider>
  );
}

export const SearchContext = createContext({
  searchValue: "",
  searchMode: false,
  setSearchMode: (mode: boolean) => {},
  setSearchValue: (value: string) => {},
  inputPlaceholder: "",
  inputDisplayValue: "",
  selectedValue: undefined
});
export function SearchProvider<T>(props: {
  children: ReactNode;
  inputTitle: string;
  inputPlaceholder: string;
}) {
  const [searchValue, setSearchValue] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const { selectedValue, getLabel } = useContext(SelectContext);
  const { inputTitle, inputPlaceholder, children } = props;
  const inputDisplayValue = useMemo(() => {
    if (searchMode) {
      return searchValue;
    } else {
      if (selectedValue && (!Array.isArray(selectedValue) || selectedValue.length == 1)) {
        return getLabel(selectedValue[0] || getLabel(selectedValue));
      } else if (Array.isArray(selectedValue) && selectedValue.length > 1) {
        return `${inputTitle}`;
      } else {
        return inputTitle;
      }
    }
  }, [selectedValue, searchValue, searchMode]);
  return (
    <SearchContext.Provider
      value={{
        inputPlaceholder,
        inputDisplayValue,
        searchMode,
        setSearchMode,
        searchValue,
        setSearchValue,
        selectedValue
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export const OptionContext = createContext({
  getChildren: defaultGetChildren,
  getValue: defaultGetValue,
  getLabel: defaultGetLable,
  isMulty: true,
  isSelected: (a: any) => {},
  isHalfSelected: (a: any) => {},
  setSelect: (a: any, remove?: boolean) => {}
});
export function OptionProvider<T>(props) {
  const { children, level = 0 } = props;
  const {
    selectedValue,
    isMulty,
    onChange,
    getChildren = defaultGetChildren,
    getValue = defaultGetValue,
    getLabel = defaultGetLable
  } = useContext(SelectContext);
  const isSelected = (item) => {
    if (isMulty) {
      if (Array.isArray(selectedValue)) {
        if (selectedValue?.map(getValue).includes(getValue(item))) {
          return true;
        }
        const children = getChildren(item);
        if (Array.isArray(children) && children.length > 0) {
          return children.reduce((foreSelected, child) => {
            return foreSelected && isSelected(child);
          }, true);
        }
      }
      return false;
    } else {
      return getValue(item) == getValue(selectedValue);
    }
  };
  const isHalfSelected = (item) => {
    if (!isMulty) {
      return false;
    }
    if (isSelected(item)) {
      return false;
    }
    if (Array.isArray(selectedValue)) {
      const children = getChildren(item);
      if (Array.isArray(children) && children.length > 0) {
        const childSelected =
          children.filter((child) => {
            return isSelected(child);
          }).length > 0;
        const childHarfSelected =
          children.filter((child) => {
            return isHalfSelected(child);
          }).length > 0;
        return childSelected || childHarfSelected;
      }
    }
    return false;
  };

  const selectItem = (item) => {
    if (!isMulty) {
      return item;
    } else {
      const children = getChildren(item);
      if (children?.length) {
        return children.reduce((list, item) => {
          return [...list, ...selectItem(item)];
        }, []);
      } else {
        return [item];
      }
    }
  };

  const deSelectItem = (item) => {
    if (!isMulty) {
      return item;
    } else {
      const children = getChildren(item);
      if (children?.length) {
        return children.reduce((list, item) => {
          return [...list, ...deSelectItem(item)];
        }, []);
      } else {
        return [item];
      }
    }
  };

  const setSelect = (item: Record<string, any>, remove = true) => {
    if (isSelected(item) && remove) {
      const result = deSelectItem(item);
      const shouldRemoved = result.map(getValue);
      const newSelectedValue = selectedValue.filter(
        (item) => !shouldRemoved?.includes?.(getValue(item))
      );
      onChange(newSelectedValue);
    } else {
      const selectedValues = selectedValue.map(getValue);
      const result = selectItem(item);
      const newSelectedValue = [
        ...selectedValue,
        ...result.filter((item) => !selectedValues?.includes?.(getValue(item)))
      ];
      onChange(newSelectedValue);
    }
  };
  return (
    <OptionContext.Provider
      value={{
        isMulty: false,
        getChildren,
        getValue,
        getLabel,
        isSelected,
        isHalfSelected,
        setSelect
      }}
    >
      {children}
    </OptionContext.Provider>
  );
}

export const ExpandContext = createContext({
  expandedItems: [],
  setExpandedItem: (item) => {},
  isExpanded: ((item) => false) as (T) => boolean
});
export function ExpandProvider<T>(props) {
  const { children } = props;
  const [expandedItems, setExpandedItems] = useState<(number | string)[]>([]);
  const { getValue } = useContext(OptionContext);
  const isExpanded = (item) => {
    const value = getValue(item);
    return expandedItems.map(getValue).includes(value);
  };
  useEffect(() => {
    setExpandedItems([]);
  }, []);
  const setExpandedItem = (item) => {
    // 多展开
    // if (isExpanded(item)) {
    //   setExpandedItems(
    //     expandedItems.filter((existItem) => {
    //       return getValue(existItem) && getValue(existItem) !== getValue(item);
    //     })
    //   );
    // } else {
    //   setExpandedItems([...expandedItems, item]);
    // }

    // 单展开
    if (isExpanded(item)) {
      setExpandedItems([]);
    } else {
      setExpandedItems([item]);
    }
  };
  return (
    <ExpandContext.Provider
      value={{
        expandedItems,
        setExpandedItem,
        isExpanded
      }}
    >
      {children}
    </ExpandContext.Provider>
  );
}

function defaultGetChildren(item) {
  return item?.children;
}

function defaultGetValue(item) {
  return item?.value;
}

function defaultGetLable(item) {
  return item?.label;
}
function defaultGetParent(item) {
  return item?.parent;
}

export default SelectProvider;
